/**
 * Ambient analytics identity.
 *
 * Every dataLayer event carries two custom dimensions — `userId` and
 * `isCustomerB2b`. They used to be optional props on each tracker, which meant
 * every call site silently opted out by forgetting to pass them and both
 * dimensions were dead in GA4. Trackers now read identity from this context
 * instead, so there is no per-call-site wiring left to forget.
 *
 * `sendEvent` / `sendFlatEvent` take it as a dedicated argument and place it at
 * event level themselves, so a call site cannot bury it inside `ecommerce` (where
 * GA4 ignores it) by spreading it into the params.
 *
 * The provider component lives in AnalyticsIdentityProvider.tsx; context and
 * hook stay here so neither file mixes component and non-component exports.
 */

import { createContext, useContext } from "react";

export interface AnalyticsIdentity {
  /**
   * The customer's analytics identifier: a D2C member's Firebase UID, or a B2B
   * member's account key. Null for guests. `isCustomerB2b` says which it is.
   */
  userId: string | null;
  isCustomerB2b: boolean;
  /**
   * A B2B member's username — their Business Central customer number (e.g.
   * "110124"), pushed as `b2b_username` so B2B activity can be joined to BC.
   * `userId` stays the account key. Absent/null for guests and D2C members.
   */
  b2bUsername?: string | null;
}

/** Guests, and any tree rendered outside the provider (e.g. in tests). */
export const ANONYMOUS_IDENTITY: AnalyticsIdentity = {
  userId: null,
  isCustomerB2b: false,
};

export const AnalyticsIdentityContext =
  createContext<AnalyticsIdentity>(ANONYMOUS_IDENTITY);

export function useAnalyticsIdentity(): AnalyticsIdentity {
  return useContext(AnalyticsIdentityContext);
}
